import axios from "axios";

import {
  preValidateDestinationCard,
  tokenizeDestinationCard,
} from "../services/api";
import type { ICardCapabilities } from "../interfaces/payment-method";
import { brandFromPan, last4 as last4Of } from "./card-brand";
import { isVgsConfigUnavailable } from "./tokenization-config";

export type PayoutCardValidationErrorCode =
  | "CARD_VALIDATION_FAILED"
  | "CARD_UNDER_REVIEW"
  | "CARD_PUSH_NOT_SUPPORTED"
  | "CARD_CORRIDOR_CONTEXT_REQUIRED"
  | "VGS_CONFIG_UNAVAILABLE"
  | "GENERIC";

export class PayoutCardValidationError extends Error {
  readonly code: PayoutCardValidationErrorCode;

  constructor(code: PayoutCardValidationErrorCode, message?: string) {
    super(message ?? code);
    this.name = "PayoutCardValidationError";
    this.code = code;
  }
}

export interface PreValidatePayoutCardInput {
  pan: string;
  expiry: string;
  cardholderName?: string;
  sourceCountry: string;
  destinationCountry: string;
}

export interface PreValidatePayoutCardResult {
  validationId: string;
  numberToken: string;
  last4: string;
  brand: string;
  capabilities?: ICardCapabilities;
}

export const preValidatePayoutCard = async (
  linkId: string,
  card: PreValidatePayoutCardInput
): Promise<PreValidatePayoutCardResult> => {
  const cleanedPan = card.pan.replace(/\D/g, "");
  const normalizedBrand = brandFromPan(cleanedPan);
  const last4 = last4Of(cleanedPan);

  const [expiryMonth, expiryYearShort] = card.expiry.split("/");
  const expiryYear = expiryYearShort ? `20${expiryYearShort}` : "";

  let tokenized;
  try {
    tokenized = await tokenizeDestinationCard(cleanedPan);
  } catch (error) {
    if (isVgsConfigUnavailable(error)) {
      throw new PayoutCardValidationError("VGS_CONFIG_UNAVAILABLE");
    }
    throw new PayoutCardValidationError("GENERIC", "Failed to tokenize card");
  }

  const { numberToken } = tokenized;

  const cardPayload = {
    tokenizedCardNumber: numberToken,
    last4,
    brand: normalizedBrand,
    expiryMonth,
    expiryYear,
    cardholderName: card.cardholderName?.trim() || undefined,
    sourceCountry: card.sourceCountry.trim().toUpperCase(),
    destinationCountry: card.destinationCountry.trim().toUpperCase(),
  };

  try {
    const pre = await preValidateDestinationCard(linkId, cardPayload);
    if (!pre?.validationId) {
      throw new PayoutCardValidationError("GENERIC");
    }
    return {
      validationId: pre.validationId,
      numberToken,
      last4,
      brand: cardPayload.brand,
      capabilities: pre.capabilities,
    };
  } catch (error) {
    if (error instanceof PayoutCardValidationError) throw error;

    if (axios.isAxiosError(error)) {
      const code = error.response?.data?.error?.code as string | undefined;
      if (
        code === "CARD_VALIDATION_FAILED" ||
        code === "CARD_UNDER_REVIEW" ||
        code === "CARD_PUSH_NOT_SUPPORTED" ||
        code === "CARD_CORRIDOR_CONTEXT_REQUIRED"
      ) {
        throw new PayoutCardValidationError(code);
      }
    }

    throw new PayoutCardValidationError("GENERIC");
  }
};

/** True when a prior destination-card dry-run no longer matches the route. */
export const isPayoutCardValidationStale = (
  validatedFor:
    | { sourceCountry: string; destinationCountry: string }
    | undefined,
  sourceCountry: string,
  destinationCountry: string
): boolean => {
  if (!validatedFor) return true;
  return (
    validatedFor.sourceCountry.toUpperCase() !== sourceCountry.toUpperCase() ||
    validatedFor.destinationCountry.toUpperCase() !==
      destinationCountry.toUpperCase()
  );
};
